import React, { useState, useEffect } from 'react';
import {
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Label,
  Row,
  Button,
  Form,
  FormFeedback,
  Alert,
  Spinner,
} from 'reactstrap';
import { Link, useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { toast } from 'react-toastify';
import { useAuth } from '../../contexts/AuthContext';
import ParticlesAuth from '../AuthenticationInner/ParticlesAuth';
import { authApi } from '../../services/api';
import { adHatasi, adNormallestir } from '../../helpers/adKurali';
import { formHatasiUygula, FormHatasi } from '../../helpers/formHatasi';

// Common countries list - UK first as primary market
const COUNTRIES = [
  { code: 'GB', name: 'United Kingdom' },
  { code: 'US', name: 'United States' },
  { code: 'DE', name: 'Germany' },
  { code: 'FR', name: 'France' },
  { code: 'IT', name: 'Italy' },
  { code: 'ES', name: 'Spain' },
  { code: 'NL', name: 'Netherlands' },
  { code: 'BE', name: 'Belgium' },
  { code: 'AT', name: 'Austria' },
  { code: 'IE', name: 'Ireland' },
  { code: 'PL', name: 'Poland' },
  { code: 'SE', name: 'Sweden' },
  { code: 'DK', name: 'Denmark' },
  { code: 'FI', name: 'Finland' },
  { code: 'NO', name: 'Norway' },
  { code: 'CH', name: 'Switzerland' },
  { code: 'PT', name: 'Portugal' },
  { code: 'GR', name: 'Greece' },
  { code: 'CZ', name: 'Czech Republic' },
  { code: 'HU', name: 'Hungary' },
  { code: 'RO', name: 'Romania' },
  { code: 'CA', name: 'Canada' },
  { code: 'AU', name: 'Australia' },
  { code: 'NZ', name: 'New Zealand' },
  { code: 'JP', name: 'Japan' },
  { code: 'TR', name: 'Turkey' },
  { code: 'AE', name: 'United Arab Emirates' },
  { code: 'SG', name: 'Singapore' },
  { code: 'OTHER', name: 'Other' },
];

const Register = () => {
  const navigate = useNavigate();
  const { register, isAuthenticated } = useAuth();

  const [passwordShow, setPasswordShow] = useState(false);
  const [confirmPasswordShow, setConfirmPasswordShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  // [KAYIT-HATA] (17 Eki 2026) hata formun ICINDE, Sign Up dugmesinin hemen ustunde gosterilir (eskiden kartin tepesindeydi ve
  // istek sirasinda sayfa unmount oldugu icin hic gorunmuyordu); 409'da giris / sifre sifirlama baglantilari
  const [error, setError] = useState<FormHatasi | null>(null);

  const handleGoogleRegister = async () => {
    try {
      setGoogleLoading(true);
      const data = await authApi.googleLogin();

      if (data.auth_url) {
        window.location.href = data.auth_url;
      } else {
        toast.error('Google registration is not configured. Please try again later.');
      }
    } catch (error) {
      console.error('Failed to initiate Google registration:', error);
      toast.error('Failed to connect to Google. Please try again.');
    } finally {
      setGoogleLoading(false);
    }
  };

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      email: '',
      first_name: '',
      last_name: '',
      country: '',
      password: '',
      confirm_password: '',
      terms: false,
    },
    validationSchema: Yup.object({
      email: Yup.string()
        .email('Invalid email format')
        .required('Please enter your email'),
      // [KAYIT-HATA] backend services/ad_kurali.py ile ayni kural (helpers/adKurali.ts): istek gitmeden alanin altinda uyari
      first_name: Yup.string()
        .required('Please enter your first name')
        .test('ad-kurali', 'Invalid name', function (v) {
          const h = adHatasi(v || '');
          return h ? this.createError({ message: h }) : true;
        }),
      last_name: Yup.string()
        .required('Please enter your last name')
        .test('ad-kurali', 'Invalid name', function (v) {
          const h = adHatasi(v || '');
          return h ? this.createError({ message: h }) : true;
        }),
      country: Yup.string()
        .required('Please select your country'),
      password: Yup.string()
        .min(8, 'Password must be at least 8 characters')
        .matches(/[A-Z]/, 'Password must contain at least one uppercase letter')
        .matches(/[a-z]/, 'Password must contain at least one lowercase letter')
        .matches(/[0-9]/, 'Password must contain at least one number')
        .required('Please enter a password'),
      confirm_password: Yup.string()
        .oneOf([Yup.ref('password')], 'Passwords must match')
        .required('Please confirm your password'),
      terms: Yup.boolean().oneOf(
        [true],
        'You must accept the terms and conditions'
      ),
    }),
    onSubmit: async (values) => {
      try {
        setLoading(true);
        setError(null);

        await register({
          email: values.email.trim(),
          first_name: adNormallestir(values.first_name),
          last_name: adNormallestir(values.last_name),
          country: values.country,
          password: values.password,
        });

        // [KAYIT-HATA] onceki denemelerin hata bildirimleri yeni sayfaya tasinmasin (17 Eki: kutusuz sayfada kuyrukta biriken
        // "Validation error" bildirimleri kayittan sonra /subscription'daki kutu baglaninca topluca gorundu)
        toast.dismiss();
        toast.success('Account created successfully! Welcome to Coderom!');

        // Navigate to setup page
        navigate('/setup');
      } catch (err: any) {
        // [KAYIT-HATA] 422 -> alanin altinda + ilk hatali alana kaydir/odakla; 409/429/ag/500 -> dugmenin ustunde sade metin;
        // her durumda kok bildirim kutusunda toast. Girilen bilgiler silinmez, dugme yeniden basilabilir.
        const h = formHatasiUygula(err, validation, {
          alanAdlari: ['email', 'first_name', 'last_name', 'country', 'password'],
          cakismaMesaji: 'This email is already registered.',
        });
        setError(h);
      } finally {
        setLoading(false);
      }
    },
  });

  document.title = 'Sign Up | Coderom Dropshipping Platform';

  return (
    <React.Fragment>
      <ParticlesAuth>
        <div className="auth-page-content mt-lg-5">
          <Container>
            <Row>
              <Col lg={12}>
                <div className="text-center mt-sm-5 mb-4 text-white-50">
                  <div>
                    <Link to="/" className="d-inline-flex align-items-center gap-2 text-decoration-none">
                      <img src="/logo-full-white.png" alt="Coderom" style={{ height: '50px', width: 'auto' }} />
                    </Link>
                  </div>
                  <p className="mt-3 fs-15 fw-medium">
                    Professional AI-Powered Listing & Repricing Automation Platform
                  </p>
                </div>
              </Col>
            </Row>

            <Row className="justify-content-center">
              <Col md={8} lg={6} xl={5}>
                <Card className="mt-4 card-bg-fill">
                  <CardBody className="p-4">
                    <div className="text-center mt-2">
                      <h5 className="text-primary">Create New Account 🚀</h5>
                      <p className="text-muted">
                        Get your free Coderom account now
                      </p>
                    </div>

                    {/* Google Sign Up Button */}
                    <div className="mt-4">
                      <Button
                        color="light"
                        className="w-100 d-flex align-items-center justify-content-center gap-2"
                        onClick={handleGoogleRegister}
                        disabled={googleLoading}
                        style={{
                          border: '1px solid #e5e7eb',
                          padding: '10px 16px',
                          fontWeight: 500,
                        }}
                      >
                        {googleLoading ? (
                          <Spinner size="sm" />
                        ) : (
                          <svg width="18" height="18" viewBox="0 0 24 24">
                            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                          </svg>
                        )}
                        {googleLoading ? 'Connecting...' : 'Sign up with Google'}
                      </Button>
                    </div>

                    {/* Divider */}
                    <div className="mt-4 text-center position-relative">
                      <div style={{ borderTop: '1px solid #e5e7eb' }}></div>
                      <span
                        className="bg-white px-2 text-muted"
                        style={{
                          position: 'relative',
                          top: '-12px',
                          fontSize: '13px',
                        }}
                      >
                        or sign up with email
                      </span>
                    </div>

                    <div className="p-2">
                      <Form
                        onSubmit={(e) => {
                          e.preventDefault();
                          validation.handleSubmit();
                          return false;
                        }}
                      >
                        {/* Email */}
                        <div className="mb-3">
                          <Label htmlFor="useremail" className="form-label">
                            Email <span className="text-danger">*</span>
                          </Label>
                          <Input
                            id="useremail"
                            name="email"
                            className="form-control"
                            placeholder="Enter email address"
                            type="email"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.email || ''}
                            invalid={
                              validation.touched.email && validation.errors.email
                                ? true
                                : false
                            }
                          />
                          {validation.touched.email && validation.errors.email ? (
                            <FormFeedback type="invalid">
                              {validation.errors.email}
                            </FormFeedback>
                          ) : null}
                        </div>

                        {/* First Name & Last Name */}
                        <Row>
                          <Col md={6}>
                            <div className="mb-3">
                              <Label htmlFor="first_name" className="form-label">
                                First Name <span className="text-danger">*</span>
                              </Label>
                              <Input
                                name="first_name"
                                type="text"
                                placeholder="Enter first name"
                                onChange={validation.handleChange}
                                onBlur={validation.handleBlur}
                                value={validation.values.first_name || ''}
                                invalid={
                                  validation.touched.first_name &&
                                  validation.errors.first_name
                                    ? true
                                    : false
                                }
                              />
                              {validation.touched.first_name &&
                              validation.errors.first_name ? (
                                <FormFeedback type="invalid">
                                  {validation.errors.first_name}
                                </FormFeedback>
                              ) : null}
                            </div>
                          </Col>
                          <Col md={6}>
                            <div className="mb-3">
                              <Label htmlFor="last_name" className="form-label">
                                Last Name <span className="text-danger">*</span>
                              </Label>
                              <Input
                                name="last_name"
                                type="text"
                                placeholder="Enter last name"
                                onChange={validation.handleChange}
                                onBlur={validation.handleBlur}
                                value={validation.values.last_name || ''}
                                invalid={
                                  validation.touched.last_name &&
                                  validation.errors.last_name
                                    ? true
                                    : false
                                }
                              />
                              {validation.touched.last_name &&
                              validation.errors.last_name ? (
                                <FormFeedback type="invalid">
                                  {validation.errors.last_name}
                                </FormFeedback>
                              ) : null}
                            </div>
                          </Col>
                        </Row>

                        {/* Country */}
                        <div className="mb-3">
                          <Label htmlFor="country" className="form-label">
                            Country <span className="text-danger">*</span>
                          </Label>
                          <Input
                            type="select"
                            name="country"
                            id="country"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.country || ''}
                            invalid={
                              validation.touched.country && validation.errors.country
                                ? true
                                : false
                            }
                          >
                            <option value="">-- Select Country --</option>
                            {COUNTRIES.map((c) => (
                              <option key={c.code} value={c.code}>
                                {c.name}
                              </option>
                            ))}
                          </Input>
                          {validation.touched.country && validation.errors.country ? (
                            <FormFeedback type="invalid">
                              {validation.errors.country}
                            </FormFeedback>
                          ) : null}
                          <small className="text-muted">
                            Used for billing and tax purposes
                          </small>
                        </div>

                        {/* Password */}
                        <div className="mb-3">
                          <Label htmlFor="userpassword" className="form-label">
                            Password <span className="text-danger">*</span>
                          </Label>
                          <div className="position-relative auth-pass-inputgroup">
                            <Input
                              name="password"
                              type={passwordShow ? 'text' : 'password'}
                              placeholder="Enter password"
                              onChange={validation.handleChange}
                              onBlur={validation.handleBlur}
                              value={validation.values.password || ''}
                              invalid={
                                validation.touched.password &&
                                validation.errors.password
                                  ? true
                                  : false
                              }
                            />
                            {validation.touched.password &&
                            validation.errors.password ? (
                              <FormFeedback type="invalid">
                                {validation.errors.password}
                              </FormFeedback>
                            ) : null}
                            <button
                              className="btn btn-link position-absolute end-0 top-0 text-decoration-none text-muted"
                              type="button"
                              onClick={() => setPasswordShow(!passwordShow)}
                            >
                              <i
                                className={
                                  passwordShow
                                    ? 'ri-eye-off-fill align-middle'
                                    : 'ri-eye-fill align-middle'
                                }
                              ></i>
                            </button>
                          </div>
                        </div>

                        {/* Confirm Password */}
                        <div className="mb-3">
                          <Label
                            htmlFor="confirmpassword"
                            className="form-label"
                          >
                            Confirm Password{' '}
                            <span className="text-danger">*</span>
                          </Label>
                          <div className="position-relative auth-pass-inputgroup mb-3">
                            <Input
                              name="confirm_password"
                              type={confirmPasswordShow ? 'text' : 'password'}
                              placeholder="Confirm password"
                              onChange={validation.handleChange}
                              onBlur={validation.handleBlur}
                              value={validation.values.confirm_password || ''}
                              invalid={
                                validation.touched.confirm_password &&
                                validation.errors.confirm_password
                                  ? true
                                  : false
                              }
                            />
                            {validation.touched.confirm_password &&
                            validation.errors.confirm_password ? (
                              <FormFeedback type="invalid">
                                {validation.errors.confirm_password}
                              </FormFeedback>
                            ) : null}
                            <button
                              className="btn btn-link position-absolute end-0 top-0 text-decoration-none text-muted"
                              type="button"
                              onClick={() =>
                                setConfirmPasswordShow(!confirmPasswordShow)
                              }
                            >
                              <i
                                className={
                                  confirmPasswordShow
                                    ? 'ri-eye-off-fill align-middle'
                                    : 'ri-eye-fill align-middle'
                                }
                              ></i>
                            </button>
                          </div>
                        </div>

                        {/* Terms & Conditions */}
                        <div className="mb-4">
                          <p className="mb-0 fs-12 text-muted fst-italic">
                            By registering you agree to the Coderom{' '}
                            <Link
                              to="/terms"
                              className="text-primary text-decoration-underline fst-normal fw-medium"
                            >
                              Terms of Use
                            </Link>
                          </p>
                        </div>

                        <div className="form-check mb-3">
                          <Input
                            className="form-check-input"
                            type="checkbox"
                            id="auth-terms-check"
                            name="terms"
                            onChange={validation.handleChange}
                            checked={validation.values.terms}
                          />
                          <Label
                            className="form-check-label"
                            htmlFor="auth-terms-check"
                          >
                            I accept the{' '}
                            <Link to="/terms" className="text-primary">
                              Terms and Conditions
                            </Link>
                          </Label>
                          {validation.touched.terms && validation.errors.terms ? (
                            <div className="text-danger fs-12 mt-1">
                              {validation.errors.terms}
                            </div>
                          ) : null}
                        </div>

                        {/* [KAYIT-HATA] Error — right above the button, never off-screen */}
                        {error && error.genel && (
                          <Alert color="danger" className="mt-3 mb-0" role="alert" data-testid="kayit-hata">
                            <i className="ri-error-warning-line me-2"></i>
                            {error.genel}
                            {error.tur === 'cakisma' && (
                              <>
                                {' '}{/* [KAYIT-GIRIS] e-posta router state ile (URL'de degil) giris kutusuna onceden dolar */}
                                <Link to="/login" state={{ email: validation.values.email.trim(), kaynak: 'kayit' }} className="alert-link">Sign in</Link> or{' '}
                                <Link to="/forgot-password" className="alert-link">reset your password</Link>.
                              </>
                            )}
                          </Alert>
                        )}

                        {/* Submit Button */}
                        <div className="mt-4">
                          <Button
                            color="primary"
                            className="w-100"
                            type="submit"
                            disabled={loading}
                          >
                            {loading ? (
                              <>
                                <Spinner size="sm" className="me-2">
                                  {' '}
                                  Loading...{' '}
                                </Spinner>
                                Creating Account...
                              </>
                            ) : (
                              <>
                                <i className="ri-user-add-line me-1"></i> Sign Up
                              </>
                            )}
                          </Button>
                        </div>
                      </Form>
                    </div>
                  </CardBody>
                </Card>

                {/* Sign In Link */}
                <div className="mt-4 text-center">
                  <p className="mb-0">
                    Already have an account?{' '}
                    <Link
                      to="/login"
                      state={{ kaynak: 'kayit' }}
                      className="fw-semibold text-primary text-decoration-underline"
                    >
                      {' '}
                      Sign In{' '}
                    </Link>
                  </p>
                </div>
              </Col>
            </Row>
          </Container>
        </div>
      </ParticlesAuth>
    </React.Fragment>
  );
};

export default Register;
